import { ComponentType, lazy, LazyExoticComponent } from "react";

interface LazyModule<TProps extends object> {
  default: ComponentType<TProps>;
}

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
] as const;

const RETRY_MARKER_PREFIX = "lazy-import-retry";

function isDynamicImportError(error: unknown): error is Error {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) =>
    message.includes(pattern.toLowerCase()),
  );
}

function getRetryMarker(key: string): string {
  const pathname = typeof window === "undefined" ? "server" : window.location.pathname;
  return `${RETRY_MARKER_PREFIX}:${key}:${pathname}`;
}

export function lazyWithRetry<TProps extends object>(
  importer: () => Promise<LazyModule<TProps>>,
  key: string,
): LazyExoticComponent<ComponentType<TProps>> {
  return lazy(async () => {
    try {
      const module = await importer();

      if (typeof window !== "undefined") {
        sessionStorage.removeItem(getRetryMarker(key));
      }

      return module;
    } catch (error: unknown) {
      if (typeof window !== "undefined" && isDynamicImportError(error)) {
        const retryMarker = getRetryMarker(key);

        if (!sessionStorage.getItem(retryMarker)) {
          sessionStorage.setItem(retryMarker, "1");
          window.location.reload();
          return new Promise<LazyModule<TProps>>(() => {});
        }

        sessionStorage.removeItem(retryMarker);
      }

      throw error;
    }
  });
}
